import CryptoJS from "crypto-js";
import type { Store } from "vuex";
import type { PersistedAuthState, RootState } from "@/types/store";

const AUTH_STORAGE_KEY = "st-admin-auth";
const LEGACY_SESSION_KEY = "ilab:session";
const LEGACY_LOCAL_KEY = "ilab";
const STORAGE_SECRET = "ILAB_ST_ADMIN_AUTH_V1_KW_!@#";
const STORAGE_VERSION = 1;

interface StoredAuthEnvelope {
  version: number;
  state: PersistedAuthState;
}

const normalizeAuthState = (value: unknown): PersistedAuthState | null => {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const accessToken =
    typeof record.access_token === "string" ? record.access_token : "";
  const refreshToken =
    typeof record.refresh_token === "string" ? record.refresh_token : "";
  const rawMall = record.mall_info;
  let mallInfo: PersistedAuthState["mall_info"] = null;

  if (rawMall && typeof rawMall === "object") {
    const mall = rawMall as Record<string, unknown>;
    if (
      typeof mall.mall_id === "string" &&
      mall.mall_id &&
      (typeof mall.shop_no === "string" || typeof mall.shop_no === "number")
    ) {
      mallInfo = {
        mall_id: mall.mall_id,
        shop_no: String(mall.shop_no),
      };
    }
  }

  const rawShop = record.shop;
  let shop: PersistedAuthState["shop"] = undefined;
  if (rawShop && typeof rawShop === "object") {
    const s = rawShop as Record<string, unknown>;
    if (typeof s.mall_id === "string" && s.mall_id) {
      shop = s as unknown as any;
    }
  }

  if (!accessToken && !refreshToken) return null;
  return {
    access_token: accessToken,
    refresh_token: refreshToken,
    mall_info: mallInfo,
    shop,
  };
};

export function loadPersistedAuthState(): PersistedAuthState | null {
  if (typeof window === "undefined") return null;
  const encrypted = window.localStorage.getItem(AUTH_STORAGE_KEY);
  if (!encrypted) return null;

  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, STORAGE_SECRET);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    const envelope = JSON.parse(decrypted) as StoredAuthEnvelope;
    if (envelope.version !== STORAGE_VERSION) return null;
    return normalizeAuthState(envelope.state);
  } catch (error) {
    console.error("Failed to load persisted auth state:", error);
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
}

export function loadLegacyAuthState(): PersistedAuthState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LEGACY_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return normalizeAuthState({
      access_token: parsed.access_token,
      refresh_token: parsed.refresh_token,
      mall_info: null,
    });
  } catch (error) {
    console.error("Failed to load legacy auth state:", error);
    return null;
  }
}

export function clearLegacyAuthStorage(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(LEGACY_SESSION_KEY);
  window.localStorage.removeItem(LEGACY_LOCAL_KEY);
}

export function clearAllAuthStorage(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  clearLegacyAuthStorage();
  document.cookie =
    "access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
}

function savePersistedAuthState(state: PersistedAuthState): void {
  if (typeof window === "undefined") return;
  if (!state.access_token && !state.refresh_token) {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
    return;
  }

  const envelope: StoredAuthEnvelope = {
    version: STORAGE_VERSION,
    state,
  };
  const encrypted = CryptoJS.AES.encrypt(
    JSON.stringify(envelope),
    STORAGE_SECRET
  ).toString();
  window.localStorage.setItem(AUTH_STORAGE_KEY, encrypted);
}

export function createAuthStoragePlugin() {
  return (store: Store<RootState>) => {
    store.subscribe((_mutation, state) => {
      try {
        savePersistedAuthState({
          access_token: state.access_token,
          refresh_token: state.refresh_token,
          mall_info: state.mall_info,
          shop: state.shop?.mall_id ? state.shop : undefined,
        });
      } catch (error) {
        console.error("Failed to persist auth state:", error);
      }
    });
  };
}
